// MoneyFoundWidget — "What your CRM did for you" recap.
//
// Mounts at the top of the home dashboard. Fires on every login
// (component mount + auth-user change). Shows the same three
// numbers the daily push and email surface:
//
//   * Hours saved                   — from AI action counts × time-savings model
//   * Money made                    — commission_transactions today
//   * Money protected               — RETAIN saves + chargeback avoidance
//
// Plus the ROI multiplier ("your $147/mo returned $2,340 this month
// — 15.9x") which is the number that converts a payer into a
// believer.
//
// Also embeds a low-friction referral CTA: if the ROI is > 3× and
// the widget has been seen more than once (repeat wow moment),
// surface "your CRM earned back its cost — send it to a friend"
// with a route to the referral tree. Wired to the tree the
// Referrals tab now hosts.
//
// Every number traces to a real API call. No fabricated placeholders.
// If the endpoint 500s the widget hides itself — never a plausible
// figure with no source.

import React from 'react';
import { readPersistedJwtDirect } from '../lib/authedFetch';
import { supabase } from '../lib/supabase';

async function bearer() {
  const token = readPersistedJwtDirect();
    return token ? { Authorization: `Bearer ${token}` } : {};
}

const LS_SEEN_COUNT = 'crm:money-found:seen-count:v1';

function fmt$(n) {
  if (!Number.isFinite(n) || n === 0) return '$0';
  if (n >= 10_000) return `$${Math.round(n / 1000)}k`;
  return `$${Math.round(n).toLocaleString()}`;
}

function fmtHrs(n) {
  if (!Number.isFinite(n) || n <= 0) return '0h';
  if (n < 1) return `${Math.round(n * 60)}min`;
  return `${n.toFixed(n < 10 ? 1 : 0)}h`;
}

export default function MoneyFoundWidget({ onGotoTab }) {
  const [state, setState] = React.useState({ loading: true, data: null, error: null });
  const [dismissed, setDismissed] = React.useState(false);
  const [seenCount, setSeenCount] = React.useState(0);

  React.useEffect(() => {
    // Bump the seen counter — used to decide whether to show the
    // referral CTA (avoid asking on the very first render).
    try {
      const n = Number(localStorage.getItem(LS_SEEN_COUNT) || 0) + 1;
      localStorage.setItem(LS_SEEN_COUNT, String(n));
      setSeenCount(n);
    } catch {}
    (async () => {
      try {
        const r = await fetch('/api/money-found/current', { headers: await bearer() });
        const j = await r.json();
        if (r.ok && j.ok) setState({ loading: false, data: j, error: null });
        else setState({ loading: false, data: null, error: j.error || `HTTP ${r.status}` });
      } catch (err) {
        setState({ loading: false, data: null, error: err.message });
      }
    })();
  }, []);

  if (state.loading) return null;
  if (state.error) return null;                 // never render a plausible zero
  if (dismissed)   return null;
  if (!state.data) return null;

  const { today, month_to_date, subscription } = state.data;

  // Show only if there's SOMETHING to say. Silence beats a
  // "you did nothing today" pep talk.
  const hasAnything = today.hours_saved > 0
                    || today.money_made_dollars > 0
                    || today.money_protected_dollars > 0
                    || month_to_date.revenue_dollars > 0;
  if (!hasAnything) return null;

  const roi = subscription?.roi_multiplier || 0;
  const showReferralCta = roi >= 3 && seenCount >= 2;

  return (
    <div className="bg-gradient-to-r from-emerald-50 via-amber-50 to-white border-2 border-amber-300 rounded-xl p-5 mb-4 relative">
      <button
        onClick={() => setDismissed(true)}
        aria-label="Dismiss"
        className="absolute top-3 right-3 text-slate-400 hover:text-slate-700 text-xs"
      >✕</button>

      <div className="text-[10px] font-black uppercase tracking-[0.28em] text-emerald-800">
        What your CRM did for you
      </div>
      <div className="text-lg md:text-xl font-black text-slate-900 mt-1">
        Today · <span className="text-emerald-700">{fmtHrs(today.hours_saved)} saved</span>
        {today.money_made_dollars > 0 && (
          <> · <span className="text-emerald-700">{fmt$(today.money_made_dollars)} earned</span></>
        )}
        {today.money_protected_dollars > 0 && (
          <> · <span className="text-amber-700">{fmt$(today.money_protected_dollars)} protected</span></>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
        <Stat label="Month revenue"          value={fmt$(month_to_date.revenue_dollars)}      tone="emerald"/>
        <Stat label="Month hours saved"      value={fmtHrs(month_to_date.hours_saved)}        tone="slate"/>
        <Stat label="AI actions today"       value={String(today.ai_actions_count || 0)}      tone="slate"/>
        {roi > 0 && (
          <Stat label={`Return on your $${subscription.dollars_per_month}/mo`} value={`${roi}x`} tone="amber"/>
        )}
      </div>

      {(today.top_wins || []).length > 0 && (
        <div className="mt-4 text-xs text-slate-700">
          <span className="font-bold uppercase tracking-wider text-[10px] text-slate-500 mr-2">Top wins</span>
          {today.top_wins.slice(0, 3).join(' · ')}
        </div>
      )}

      {showReferralCta && (
        <div className="mt-5 pt-4 border-t border-amber-200 flex items-center gap-3 flex-wrap">
          <div className="text-sm text-slate-800">
            Your CRM has earned back its cost <strong>{roi}x over</strong> this month.
            Know an agent who'd want the same?
          </div>
          <button
            onClick={() => onGotoTab?.('referrals')}
            className="ml-auto bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-lg"
          >
            Send them the CRM →
          </button>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, tone = 'slate' }) {
  const toneCls =
    tone === 'emerald' ? 'text-emerald-800' :
    tone === 'amber'   ? 'text-amber-800'   :
    'text-slate-800';
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-xl font-black tabular-nums ${toneCls}`}>{value}</div>
    </div>
  );
}
